import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  getOperationPackageCountInProgress,
  getOperationPackageInstances,
  getOperationPackagePatterns,
  getOperationPackages,
  getOperationTaskCountInProgress,
  postOperationPackagePatterns,
} from "@/features/operation-packages/api";
import type {
  OwnerProduct,
  PackageType,
} from "@/features/operation-packages/api";
import { queryClient } from "@/lib/queryClient";

// --- Query Keys ---

export const operationPackageQueryKeys = {
  all: (ownerProduct: OwnerProduct) =>
    ["operation-packages", ownerProduct] as const,
  lists: (ownerProduct: OwnerProduct) =>
    [...operationPackageQueryKeys.all(ownerProduct), "list"] as const,
  list: (ownerProduct: OwnerProduct) =>
    [...operationPackageQueryKeys.lists(ownerProduct)] as const,
  countInProgress: (ownerProduct: OwnerProduct, id: string) =>
    [
      ...operationPackageQueryKeys.all(ownerProduct),
      id,
      "count-in-progress",
    ] as const,
  patterns: (ownerProduct: OwnerProduct, id: string) =>
    [...operationPackageQueryKeys.all(ownerProduct), id, "patterns"] as const,
  patternCountInProgress: (
    ownerProduct: OwnerProduct,
    id: string,
    patternId: string,
  ) =>
    [
      ...operationPackageQueryKeys.patterns(ownerProduct, id),
      patternId,
      "count-in-progress",
    ] as const,
  instances: (ownerProduct: OwnerProduct, patternId: string) =>
    [
      ...operationPackageQueryKeys.all(ownerProduct),
      "instances",
      patternId,
    ] as const,
} as const;

// --- Hooks ---

/**
 * Operation Packageの一覧を取得する
 */
export const useGetOperationPackages = (ownerProduct: OwnerProduct) => {
  const { data, isLoading, error } = useQuery({
    queryKey: operationPackageQueryKeys.list(ownerProduct),
    queryFn: () => getOperationPackages(ownerProduct),
  });
  return { data, isLoading, error };
};

/**
 * 指定されたパッケージタイプのパッケージIDを取得するReact Queryフック
 * @param ownerProduct プロダクト
 * @param packageType 取得したいパッケージのタイプ
 */
export function useOperationPackageIdByType(
  ownerProduct: OwnerProduct,
  packageType: PackageType,
) {
  // 全パッケージを取得
  const {
    data: allPackages,
    error,
    isLoading,
  } = useGetOperationPackages(ownerProduct);

  // パッケージタイプに対応するIDをメモ化して算出
  const packageId = useMemo(() => {
    if (!allPackages || !packageType) return null;

    const targetPackage = allPackages.find(
      (pkg) => pkg.packageType === packageType,
    );
    if (targetPackage) {
      return targetPackage.operationPackageId;
    }
    return null;
  }, [allPackages, packageType]);

  return {
    data: packageId,
    error,
    isLoading,
    isSuccess: !isLoading && !error,
    isError: !!error,
    isLoadingError: !!error,
  };
}

/**
 * Operation Event内の実行中のInstanceの数を取得する
 */
export const useGetOperationPackageCountInProgress = (
  ownerProduct: OwnerProduct,
  id: string,
) => {
  const { data, isLoading, error } = useQuery({
    queryKey: operationPackageQueryKeys.countInProgress(ownerProduct, id),
    queryFn: () => getOperationPackageCountInProgress(ownerProduct, id),
    enabled: !!id,
  });
  return { data, isLoading, error };
};

/**
 * Operation Packageのパターンの定義一覧を取得する
 */
export const useGetOperationPackagePatterns = (
  ownerProduct: OwnerProduct,
  id: string,
  options?: { enabled?: boolean },
) => {
  const { data, isLoading, error } = useSuspenseQuery({
    queryKey: operationPackageQueryKeys.patterns(ownerProduct, id),
    queryFn: () => {
      if (options?.enabled === false || !id) {
        return Promise.resolve([]);
      }
      return getOperationPackagePatterns(ownerProduct, id);
    },
  });
  return { data, isLoading, error };
};

/**
 * 特定のOperation Packageのパターンを作成する
 */
export const usePostOperationPackagePatterns = (
  ownerProduct: OwnerProduct,
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  },
) => {
  const { mutate, isPending, error } = useMutation({
    mutationFn: () => postOperationPackagePatterns(ownerProduct, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operationPackageQueryKeys.patterns(ownerProduct, id),
      });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
  return { mutate, isPending, error };
};

/**
 * 特定のOperation Packageのパターンのタスクの実行中の数を取得する
 */
export const useGetOperationTaskCountInProgress = ({
  ownerProduct,
  operationPackageId: id,
  patternId,
}: {
  ownerProduct: OwnerProduct;
  operationPackageId: string;
  patternId: string;
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: operationPackageQueryKeys.patternCountInProgress(
      ownerProduct,
      id,
      patternId,
    ),
    queryFn: () => getOperationTaskCountInProgress(ownerProduct, id, patternId),
  });
  return { data, isLoading, error };
};

/**
 * 特定のOperation Packageのパターンのインスタンスの一覧を取得する
 */
export const useGetOperationPackageInstances = (
  ownerProduct: OwnerProduct,
  patternId: string,
  options?: { enabled?: boolean },
) => {
  const { data, isPending, error } = useQuery({
    queryKey: operationPackageQueryKeys.instances(ownerProduct, patternId),
    queryFn: () => getOperationPackageInstances(ownerProduct, patternId),
    enabled: options?.enabled ?? true,
  });
  return { data, isPending, error };
};
